import { useState, useEffect, useRef } from "react";

import {
  Box,
} from '@mui/material';

const CircularGauge = ({
  value = 65,
  max = 100,
  size = 275,
  tickCount = 60,
  activeColor = "#E26438",
  inactiveColor = "#DADADA",
  noShadow,
}) => {
  const [animatedValue, setAnimatedValue] = useState(0);
  const animRef = useRef(null);

  useEffect(() => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    const duration = 1400;
    const startTime = performance.now();
    const animate = (now) => {
      const t = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 4);
      setAnimatedValue(eased * value);
      if (t < 1) animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, [value]);

  const displayValue = Math.round(animatedValue);
  const ratio = animatedValue / max;

  // All proportions derived from 275px reference: disc=175, tickH=25, tickW=4, gap=8
  const discSize = size * (175 / 275);
  const tickH    = size * (25 / 275);
  const tickW    = size * (4 / 275);
  const gap      = size * (20 / 275);
  const tickInnerR = discSize / 2 + gap;
  const radius     = tickInnerR + tickH / 2;

  const ticks = Array.from({ length: tickCount }, (_, i) => {
    const angleDeg = (i / tickCount) * 360 - 90;
    const isActive = i / tickCount < ratio;
    return { angleDeg, isActive };
  });

  return (
    <Box style={{
      position: "relative",
      width: size,
      height: size,
      borderRadius: "50%",
      background: "transparent",
    }}>
      {/* Ticks */}
      {ticks.map(({ angleDeg, isActive }, i) => {
        const rad = (angleDeg * Math.PI) / 180;
        const cx = size / 2 + radius * Math.cos(rad);
        const cy = size / 2 + radius * Math.sin(rad);
        return (
          <Box
            key={i}
            style={{
              position: "absolute",
              width: tickW,
              height: tickH,
              borderRadius: 0,
              background: isActive ? activeColor : inactiveColor,
              left: cx - tickW / 2,
              top: cy - tickH / 2,
              transformOrigin: "center center",
              transform: `rotate(${angleDeg + 90}deg)`,
            }}
          />
        );
      })}

      {/* Inner disc */}
      <Box style={{
        position: "absolute",
        width: discSize,
        height: discSize,
        borderRadius: "50%",
        background: "white",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        boxShadow: noShadow?"":"0 4px 24px rgba(0,0,0,0.10), 0 1px 6px rgba(0,0,0,0.07)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: discSize * 0.04,
      }}>
        {/* Big number */}
        <Box style={{
          fontSize: discSize * 0.32,
          color: "black",
          letterSpacing: "-1px",
          lineHeight: 1,
        }}>
          {displayValue}
        </Box>

        {/* Badge pill */}
        <Box sx={{
          background: "#FAF8F6",
          borderRadius: 999,
          padding: `${discSize * 0.039}px ${discSize * 0.05}px`,
          fontSize: discSize * 0.095,
          color: "black",
        }}>
          {`${displayValue}/${max}`}
        </Box>
      </Box>
    </Box>
  );
};

export default CircularGauge;
